using @rabbita {type Cmd, type Emit, type Html, new, none}
using @html {
  a, aside, button, div, h1, h2, h3, input, main_, p, section, small,
  span, text, type Target,
}

///|
extern "js" fn encode_uri_component(value : String) -> String =
  #| function(value) { return encodeURIComponent(value); }

///|
extern "js" fn format_date(value : String?) -> String =
  #| function(value) {
  #|   if (!value) return "방송일 미상";
  #|   const date = new Date(value);
  #|   if (Number.isNaN(date.getTime())) return value;
  #|   return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;
  #| }

///|
struct Meta { count : Int } derive(FromJson)

///|
struct Facet { name : String; count : Int } derive(FromJson)

///|
struct Facets {
  genres : Array[Facet]
  regions1 : Array[Facet]
  regions2 : Array[Facet]
} derive(FromJson)

///|
struct Restaurant {
  rowId : String
  name : String
  addressCandidate : String?
  region1 : String?
  region2 : String?
  genre : String?
  foods : Array[String]
  videoTitle : String?
  datePublished : String?
  url : String?
  needsAddressVerification : Bool?
} derive(FromJson)

///|
struct MuktenData {
  meta : Meta
  facets : Facets
  restaurants : Array[Restaurant]
} derive(FromJson)

///|
enum DataState { Loading; Failed(String); Ready(MuktenData) }

///|
enum FacetKind { Genre; Region1; Region2 }

///|
enum UiMsg {
  DataResponse(Result[MuktenData, Error])
  Search(String)
  ToggleFacet(FacetKind, String)
  Select(String)
  ClearFilters
}

///|
struct Filters { query : String; genre : String?; region1 : String?; region2 : String? }

///|
struct AppState { data : DataState; filters : Filters; selectedRowId : String? }

///|
fn initial_state() -> AppState {
  { data: Loading, filters: { query: "", genre: None, region1: None, region2: None }, selectedRowId: None }
}

///|
fn first_row_id(data : MuktenData) -> String? { data.restaurants.get(0).map(r => r.rowId) }

///|
fn update(_emit : Emit[UiMsg], msg : UiMsg, state : AppState) -> (Cmd, AppState) {
  let next = match msg {
    DataResponse(Ok(data)) => { ..state, data: Ready(data), selectedRowId: first_row_id(data) }
    DataResponse(Err(error)) => {
      ..state,
      data: Failed(
        "restaurants.json을 MoonBit 타입으로 디코딩하지 못했습니다. (Error: \{error})",
      ),
    }
    Search(query) => { ..state, filters: { ..state.filters, query, } }
    ToggleFacet(kind, value) => match kind {
      Genre => { ..state, filters: { ..state.filters, genre: toggle(state.filters.genre, value) } }
      Region1 => { ..state, filters: { ..state.filters, region1: toggle(state.filters.region1, value), region2: None } }
      Region2 => { ..state, filters: { ..state.filters, region2: toggle(state.filters.region2, value) } }
    }
    Select(row_id) => { ..state, selectedRowId: Some(row_id) }
    ClearFilters => { ..state, filters: { query: "", genre: None, region1: None, region2: None } }
  }
  (none, next)
}

///|
fn toggle(current : String?, value : String) -> String? {
  if current == Some(value) { None } else { Some(value) }
}

///|


///|
fn opt(value : String?, fallback? : String = "") -> String { match value { Some(s) => s; None => fallback } }

///|
fn opt_bool(value : Bool?) -> Bool { match value { Some(b) => b; None => false } }

///|
fn includes(haystack : String, needle : String) -> Bool { haystack.to_lower().contains(needle.to_lower()) }

///|
fn text_matches(r : Restaurant, raw_query : String) -> Bool {
  let query = raw_query.trim().to_owned()
  query == "" || [r.name, opt(r.addressCandidate), opt(r.videoTitle), r.foods.join(" "), opt(r.genre), opt(r.region1), opt(r.region2)].any(part => includes(part, query))
}

///|
fn restaurant_matches(r : Restaurant, filters : Filters) -> Bool {
  text_matches(r, filters.query) && (filters.genre == None || r.genre == filters.genre) && (filters.region1 == None || r.region1 == filters.region1) && (filters.region2 == None || r.region2 == filters.region2)
}

///|
fn filtered_restaurants(data : MuktenData, filters : Filters) -> Array[Restaurant] {
  let results = data.restaurants.filter(r => restaurant_matches(r, filters))
  results.sort_by((a, b) => (opt(a.region2) + "|" + opt(a.genre) + "|" + a.name).lexical_compare(opt(b.region2) + "|" + opt(b.genre) + "|" + b.name))
  results
}

///|
fn find_by_row_id(items : Array[Restaurant], row_id : String) -> Restaurant? {
  for item in items { if item.rowId == row_id { return Some(item) } }
  None
}

///|
fn selected_restaurant(results : Array[Restaurant], selected_row_id : String?) -> Restaurant? {
  match selected_row_id {
    Some(row_id) => match find_by_row_id(results, row_id) { Some(r) => Some(r); None => results.get(0) }
    None => results.get(0)
  }
}

///|
fn region2_items(data : MuktenData, region1 : String?) -> Array[Facet] {
  data.facets.regions2.filter(item => match region1 { Some(region) => item.name == region || item.name.has_prefix(region + " "); None => true })
}

///|
fn take_facets(items : Array[Facet], limit : Int) -> Array[Facet] {
  let out : Array[Facet] = []
  let n = items.length().min(limit)
  for i = 0; i < n; i = i + 1 { out.push(items[i]) }
  out
}

///|
fn classes(base : String, active : Bool) -> String { if active { base + " active" } else { base } }

///|
fn chip(kind : FacetKind, selected : String?, item : Facet, emit : Emit[UiMsg]) -> Html {
  button(class=classes("chip", selected == Some(item.name)), on_click=emit(ToggleFacet(kind, item.name)), [text(item.name), span(class="count", item.count.to_string())])
}

///|
fn chips(kind : FacetKind, selected : String?, items : Array[Facet], emit : Emit[UiMsg]) -> Html {
  div(class="chips", items.map(item => chip(kind, selected, item, emit)))
}

///|
fn food_badges(foods : Array[String]) -> Array[Html] {
  let out : Array[Html] = []
  let n = foods.length().min(5)
  for i = 0; i < n; i = i + 1 { out.push(span(class="badge", foods[i])) }
  out
}

///|
fn card(selected : Restaurant?, r : Restaurant, emit : Emit[UiMsg]) -> Html {
  let is_selected = match selected { Some(s) => s.rowId == r.rowId; None => false }
  let meta = [span(class="badge region", opt(r.region2, fallback="미분류")), span(class="badge", opt(r.genre, fallback="미분류"))]
  if opt_bool(r.needsAddressVerification) { meta.push(span(class="badge", "주소검증")) }
  div(class=if is_selected { "card selected" } else { "card" }, on_click=emit(Select(r.rowId)), [
    h3(r.name),
    div(class="meta", meta),
    div(class="address", opt(r.addressCandidate, fallback="주소 후보 없음")),
    if r.foods.length() > 0 { div(class="meta", food_badges(r.foods)) } else { @html.nothing },
    div(class="broadcast-meta", [
      span(class="broadcast-date", "방송 " + format_date(r.datePublished)),
      a(class="video-link", href=opt(r.url, fallback="#"), target=Target::Blank, rel="noreferrer", "YouTube 영상 보기"),
    ]),
  ])
}

///|
fn detail_row(label : String, value : String) -> Html {
  div(class="detail-row", [small(label), text(value)])
}

///|
fn detail(selected : Restaurant?) -> Html {
  match selected {
    Some(r) => aside(class="detail-panel", [
      div(class="eyebrow", "Selected restaurant"),
      h2(r.name),
      detail_row("음식장르", opt(r.genre, fallback="미분류")),
      detail_row("위치", opt(r.region2, fallback="미분류")),
      detail_row("주소 후보", opt(r.addressCandidate, fallback="주소 후보 없음")),
      detail_row("음식/메뉴", if r.foods.length() > 0 { r.foods.join(", ") } else { "미분류" }),
      detail_row("방송", opt(r.videoTitle)),
      detail_row("게시일", opt(r.datePublished, fallback="-")),
      div(class="actions", [
        a(class="action", href=opt(r.url, fallback="#"), target=Target::Blank, rel="noreferrer", "YouTube"),
        a(class="action secondary", href="https://map.naver.com/p/search/" + encode_uri_component(r.name), target=Target::Blank, rel="noreferrer", "NAVER 검색"),
      ]),
    ])
    None => aside(class="detail-panel", p("식당 카드를 선택하세요."))
  }
}

///|
fn sidebar(data : MuktenData, filters : Filters, emit : Emit[UiMsg]) -> Html {
  let r2 = take_facets(region2_items(data, filters.region1), 18)
  let children : Array[Html] = [
    input(class="search", placeholder="식당, 음식, 위치 검색", value=filters.query, on_input=s => emit(Search(s))),
    div(class="section-title", [span("음식장르"), small("genre")]),
  ]
  children.push(chips(Genre, filters.genre, data.facets.genres, emit))
  children.push(div(class="section-title", [span("광역 위치"), small("region 1")]))
  children.push(chips(Region1, filters.region1, take_facets(data.facets.regions1, 12), emit))
  children.push(div(class="section-title", [span("세부 위치"), small("region 2")]))
  children.push(chips(Region2, filters.region2, r2, emit))
  children.push(button(class="clear-btn", on_click=emit(ClearFilters), "필터 초기화"))
  aside(class="sidebar", children)
}

///|
fn stats(data : MuktenData) -> Html {
  div(class="stats", [
    div(class="stat", [@html.b(data.meta.count.to_string()), span("restaurant rows")]),
    div(class="stat", [@html.b(data.facets.genres.length().to_string()), span("genres")]),
    div(class="stat", [@html.b(data.facets.regions2.length().to_string()), span("regions")]),
  ])
}

///|
fn ready_view(emit : Emit[UiMsg], state : AppState, data : MuktenData) -> Html {
  let filters = state.filters
  let results = filtered_restaurants(data, filters)
  let selected = selected_restaurant(results, state.selectedRowId)
  let result_count = results.length().to_string() + " results"
  let path_text = "장르 " + opt(filters.genre, fallback="전체") + " / 광역 " + opt(filters.region1, fallback="전체") + " / 세부 " + opt(filters.region2, fallback="전체")
  let result_body = if results.length() == 0 {
    div(class="empty-state", [h3("검색 결과가 없습니다"), p("검색어를 줄이거나 필터를 초기화해 다시 훑어보세요.")])
  } else {
    div(class="cards", results.map(r => card(selected, r, emit)))
  }
  main_(class="app-shell moon-mode mode-anthropic", [
    section(class="hero", [
      div([ 
        h1([text("먹을텐데 archive")]),
        p("Youtube 의 정보를 바탕으로 JSON-LD 로 만들었습니다."),
      ]),
      stats(data),
    ]),
    div(class="layout", [
      sidebar(data, filters, emit),
      section(class="content", [
        div(class="results", [
          div(class="toolbar", div([div(class="section-title", [span("식당 목록"), small(result_count)]), div(class="path", path_text)])),
          result_body,
        ]),
        detail(selected),
      ]),
    ]),
  ])
}

///|
fn view(emit : Emit[UiMsg], state : AppState) -> Html {
  match state.data {
    Loading => main_(class="app-shell", [section(class="hero", div([div(class="eyebrow", "Mukten archive, Rabbita"), h1("Loading..."), p("Rabbita/MoonBit 앱이 restaurant archive를 가져오는 중입니다.")]))])
    Failed(message) => main_(class="app-shell", [section(class="hero", div([div(class="eyebrow", "Mukten archive, Rabbita"), h1("Error"), p(message)]))])
    Ready(data) => ready_view(emit, state, data)
  }
}

///|
fn load_data(emit : Emit[UiMsg]) -> Cmd {
  @http.get("/data/restaurants.json").expect_json(result =>
    emit(DataResponse(result)),
  )
}

///|
fn main {
  let (emit, app) = @rabbita.cell_with_emit(model=initial_state(), update~, view~)
  new(app)..with_init(load_data(emit)).mount("app")
}
